// Step-by-step attempt / service entry: one question per screen, tap an answer
// and it moves on. ‹ Back (or swipe right) returns to the previous screen with
// the answers kept. The logic lives in lib/attemptWizard.js; this file only
// draws it. App.jsx supplies the parts it already owns (photos, AB 747
// statement, due-diligence check, mailing, Save Entry) as slots.
import { useEffect, useRef } from "react";
import { STEPS, OTHER, applyAnswer, currentStep, nextStep, prevStep, varyAttempts } from "./lib/attemptWizard";
import { BUSINESS_NOT_AUTHORIZED_MSG } from "./lib/attemptWording";
import DatePick from "./DatePick";

export default function AttemptWizard({ form, setForm, subjectName, otherNames, attempts, slots, onCancel, address }) {
  const step = currentStep(form);
  const def = STEPS[step] || STEPS.start;
  const boxRef = useRef(null);
  const touch = useRef(null);
  const s = slots || {};

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const top = el.getBoundingClientRect().top;
    if (top < 0 && typeof window !== "undefined") window.scrollBy(0, top - 70);
  }, [step]);

  const go = (patch) => setForm(f => { const m = applyAnswer(f, patch); return { ...m, wzAt: nextStep(m, step) }; });
  const set = (patch) => setForm(f => applyAnswer(f, { ...patch, wzAt: step }));
  const back = () => setForm(f => { const p = prevStep(f, currentStep(f)); return p ? { ...f, wzAt: p } : f; });
  const prev = prevStep(form, step);

  const onTouchStart = (e) => { const t = e.touches && e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY } : null; };
  const onTouchEnd = (e) => {
    const a = touch.current; const t = e.changedTouches && e.changedTouches[0];
    touch.current = null;
    if (!a || !t || !prev) return;
    const dx = t.clientX - a.x, dy = t.clientY - a.y;
    if (dx > 70 && Math.abs(dy) < 60) back();
  };

  const Row = ({ label, on, multi, onClick, testid }) => (
    <button type="button" className={"wiz-row" + (on ? " on" : "")} data-testid={testid} onClick={onClick}>
      <span>{label}</span>
      <span className={multi ? "wiz-check" : "wiz-radio"} aria-hidden="true">{on ? (multi ? "✓" : "●") : ""}</span>
    </button>
  );
  const NextBtn = ({ label, disabled, onClick }) => (
    <button type="button" className="btn-primary wiz-next" data-testid="wiz-next" disabled={disabled} onClick={onClick || (() => go({}))}>{label || "Next ›"}</button>
  );

  // WHEN + WHERE: filled in automatically (the moment the entry was started,
  // and the job's address); shown on the last screen with a Change link.
  const fmtWhen = () => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(form.date || ""); const t = /^(\d{1,2}):(\d{2})/.exec(form.time || "");
    if (!m) return "No date";
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), t ? Number(t[1]) : 0, t ? Number(t[2]) : 0);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };
  const shownAddress = String(form.locationOverride || "").trim() || address || "";
  const whenBox = (
    <div className="serve-section" data-testid="wiz-when-where" style={{ marginBottom: "0.6rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <div>📅 <strong>{fmtWhen()}</strong></div>
          {shownAddress && <div style={{ marginTop: "0.2rem" }}>📍 {shownAddress}</div>}
        </div>
        <button type="button" className="wiz-link" data-testid="wiz-change-when" onClick={() => set({ wzEditWhen: !form.wzEditWhen })}>{form.wzEditWhen ? "Done" : "Change"}</button>
      </div>
      {form.wzEditWhen && (
        <div className="wiz-fields">
          <div className="field"><label>Date</label><DatePick value={form.date || ""} onChange={v => set({ date: v, whenEdited: true })} testid="wiz-date" /></div>
          {/* Hour / minute / AM-PM pickers: the phone's own time box didn't
              open a keyboard or picker for Kari, so these use plain lists. */}
          {(() => {
            const m = /^(\d{1,2}):(\d{2})/.exec(form.time || "");
            const h24 = m ? Number(m[1]) : 12, mm = m ? m[2] : "00";
            const ap = h24 >= 12 ? "PM" : "AM";
            const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
            const put = (h, mi, a) => { const hh = (Number(h) % 12) + (a === "PM" ? 12 : 0); set({ time: `${String(hh).padStart(2, "0")}:${mi}`, whenEdited: true }); };
            const sel = { flex: 1, minWidth: 0, fontSize: "1.15rem", padding: "0.6rem", minHeight: 52 };
            return (
              <div className="field"><label>Time</label>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }} data-testid="wiz-time">
                  <select aria-label="Hour" value={h12} onChange={e => put(e.target.value, mm, ap)} style={sel}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <span style={{ fontSize: "1.3rem", fontWeight: 700 }}>:</span>
                  <select aria-label="Minute" value={mm} onChange={e => put(h12, e.target.value, ap)} style={sel}>
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(x => <option key={x} value={x}>{x}</option>)}
                  </select>
                  <select aria-label="AM or PM" value={ap} onChange={e => put(h12, mm, e.target.value)} style={sel}>
                    <option value="AM">AM</option><option value="PM">PM</option>
                  </select>
                </div>
              </div>
            );
          })()}
          <div className="field"><label>Address (if different)</label>
            <input data-testid="wiz-address" value={form.locationOverride || ""} placeholder={address || "Address"} onChange={e => set({ locationOverride: e.target.value })} /></div>
        </div>
      )}
    </div>
  );

  let body = null;
  if (def.kind === "start") {
    const v = varyAttempts(attempts);
    const chip = (on, icon, label) => (
      <div className={"wiz-vary" + (on ? " on" : "")}><span className="wiz-vary-i">{icon}</span>{label}{on && <span className="wiz-vary-ok">✓</span>}</div>
    );
    body = (<>
      <div className="wiz-sub">Vary Your Attempts</div>
      <div className="wiz-vary-row" data-testid="vary-attempts">
        {chip(v.morning, "🌅", "Morning")}{chip(v.evening, "🌙", "Evening")}{chip(v.weekends, "📅", "Weekends")}{chip(v.newDays, "🆕", "New Days")}
      </div>
      <div className="wiz-start">
        <button type="button" className="wiz-big wiz-served" data-testid="wiz-served" onClick={() => go({ wzPath: "served", wzSubject: subjectName || "" })}>Served</button>
        <button type="button" className="wiz-big wiz-attempted" data-testid="wiz-attempted" onClick={() => go({ wzPath: "attempted", wzSubject: subjectName || "" })}>Attempted</button>
      </div>
    </>);
  } else if (def.kind === "pick") {
    let options = def.options || [];
    if (step === "deliveredTo") {
      options = [{ value: "subject", label: subjectName || "Person to serve" }]
        .concat((otherNames || []).filter(Boolean).map(n => ({ value: `person:${n}`, label: n })))
        .concat([{ value: "contact", label: "New Contact" }, { value: "posted", label: "Posted" }]);
    }
    const cur = form[def.key];
    const chosen = options.find(o => o.value === cur);
    const otherKey = `${def.key}Other`;
    body = (<>
      {options.map(o => (
        <Row key={o.value} label={o.label} on={cur === o.value} testid={`wiz-opt-${step}`}
          onClick={() => {
            if (o.text) { set({ [def.key]: o.value }); return; }
            if (step === "bizAuth" && o.value === "no") { set({ [def.key]: "no" }); return; }
            const extra = {};
            if (step === "deliveredTo" && o.value.indexOf("person:") === 0) { extra.wzContactName = o.value.slice(7); }
            if (step === "deliveredTo" && o.value === "contact" && String(cur || "").indexOf("person:") === 0) { extra.wzContactName = ""; }
            go({ [def.key]: o.value, ...extra });
          }} />
      ))}
      {chosen && chosen.text && (
        <div className="wiz-fields">
          <div className="field"><label>{chosen.text}</label>
            <input data-testid="wiz-other-text" value={form[otherKey] || ""} onChange={e => set({ [otherKey]: e.target.value })} placeholder="Type it in" autoFocus />
          </div>
          <NextBtn disabled={!String(form[otherKey] || "").trim()} />
        </div>
      )}
      {step === "bizAuth" && cur === "no" && (
        <div className="alert alert-warn" data-testid="business-not-authorized" style={{ marginTop: "0.75rem" }}>{BUSINESS_NOT_AUTHORIZED_MSG}</div>
      )}
    </>);
  } else if (def.kind === "multi") {
    const chosen = Array.isArray(form[def.key]) ? form[def.key] : [];
    const toggle = (v) => {
      let nextList;
      if (chosen.includes(v)) nextList = chosen.filter(x => x !== v);
      else if (def.none && v === def.none) nextList = [v];
      else nextList = chosen.filter(x => x !== def.none).concat([v]);
      set({ [def.key]: nextList });
    };
    body = (<>
      <div className="wiz-hint">Check all that apply</div>
      {def.options.map(o => <Row key={o.value} label={o.label} multi on={chosen.includes(o.value)} onClick={() => toggle(o.value)} testid={`wiz-opt-${step}`} />)}
      <NextBtn label="Done ›" />
    </>);
  } else if (def.kind === "fields") {
    const missing = def.fields.some(fl => fl.required && !String(form[fl.key] || "").trim());
    body = (<div className="wiz-fields">
      {def.fields.map(fl => (
        <div className="field" key={fl.key}><label>{fl.label}</label>
          <input data-testid={step === "bizName" && fl.key === "wzContactName" ? "business-served-name" : `wiz-${fl.key}`} value={form[fl.key] || ""} onChange={e => set({ [fl.key]: e.target.value })} placeholder={fl.placeholder} />
        </div>
      ))}
      {(step === "contact" || step === "postingOrder") && s.diligence}
      <NextBtn disabled={missing} />
    </div>);
  } else if (def.kind === "info") {
    body = (<>{step === "mailing" && s.mailing}<div className="wiz-hint">You can fill this in now or later.</div><NextBtn /></>);
  } else if (def.kind === "declaration") {
    body = (<div className="wiz-fields">
      <textarea data-testid="wiz-declaration" className="wiz-decl" value={form.desc || ""}
        onChange={e => setForm(f => ({ ...f, desc: e.target.value, wzDeclEdited: true }))} />
      {form.wzDeclEdited && (
        <button type="button" className="wiz-link" onClick={() => set({ wzDeclEdited: false })}>↺ Rewrite from my answers</button>
      )}
      <NextBtn disabled={!String(form.desc || "").trim()} />
    </div>);
  } else if (def.kind === "photos") {
    body = (<>{whenBox}{s.photos}{s.statement}{s.footer}{s.save}</>);
  }

  const title = step === "declaration"
    ? (form.wzPath === "served" && String(form.wzDelivered || "") !== "posted" ? `${form.wzDelivered === "subject" ? "Personal" : "Substitute"} service on ${subjectName || "the person to serve"}` : "Service Attempt Declaration")
    : def.title;

  return (
    <div className="wiz" ref={boxRef} data-testid="attempt-wizard" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="wiz-top">
        <button type="button" className="wiz-back" data-testid="wiz-back" onClick={back} disabled={!prev} style={{ visibility: prev ? "visible" : "hidden" }}>‹ Back</button>
        <div className="wiz-who">{subjectName || "Person to serve"}</div>
        <button type="button" className="wiz-cancel" data-testid="wiz-cancel" onClick={onCancel}>Cancel</button>
      </div>
      <div className="wiz-title">{title}</div>
      <div className="wiz-body">{body}</div>
    </div>
  );
}

export { OTHER };
